import { FC } from 'react'
import { useGetList } from 'react-admin'
import { Box, Checkbox, Chip, Fade, Typography, alpha } from '@mui/material'
import { OrganizationOut } from '../../../types/apiRequests'
import { FLOW } from './tokens'

interface OrganizationCheckboxListProps {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}

const OrganizationCheckboxList: FC<OrganizationCheckboxListProps> = ({ selectedIds, onChange }) => {
  const { data: orgs } = useGetList<OrganizationOut>('organization', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'display_name', order: 'ASC' },
  })

  const allIds = (orgs ?? []).map((org) => String(org.id))
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id))
  const noneSelected = selectedIds.length === 0

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  return (
    <Fade in timeout={200}>
      <Box sx={{
        border: `1px solid ${FLOW.grayBorder}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1,
          bgcolor: FLOW.grayBg,
          borderBottom: `1px solid ${FLOW.grayBorder}`,
        }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: FLOW.gray,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: '0.65rem',
            }}
          >
            Organisaties ({selectedIds.length}/{allIds.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="Alles"
              size="small"
              onClick={() => onChange([...allIds])}
              sx={{
                'height': 24, 'fontSize': '0.72rem', 'fontWeight': 600,
                'borderRadius': '6px', 'cursor': 'pointer',
                ...(allSelected
                  ? { bgcolor: alpha(FLOW.blue, 0.12), color: FLOW.blue }
                  : { bgcolor: 'transparent', color: FLOW.gray, border: `1px solid ${FLOW.grayBorder}` }),
                '&:hover': { bgcolor: alpha(FLOW.blue, 0.08) },
              }}
            />
            <Chip
              label="Geen"
              size="small"
              onClick={() => onChange([])}
              sx={{
                'height': 24, 'fontSize': '0.72rem', 'fontWeight': 600,
                'borderRadius': '6px', 'cursor': 'pointer',
                ...(noneSelected
                  ? { bgcolor: alpha(FLOW.blue, 0.12), color: FLOW.blue }
                  : { bgcolor: 'transparent', color: FLOW.gray, border: `1px solid ${FLOW.grayBorder}` }),
                '&:hover': { bgcolor: alpha(FLOW.blue, 0.08) },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ maxHeight: 240, overflow: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {(orgs ?? []).map((org) => {
            const id = String(org.id)
            const checked = selectedIds.includes(id)
            return (
              <Box
                key={id}
                onClick={() => handleToggle(id)}
                sx={{
                  'display': 'flex', 'alignItems': 'center', 'gap': 0.5,
                  'px': 1.5, 'py': 0.75,
                  'cursor': 'pointer', 'userSelect': 'none',
                  'borderBottom': `1px solid ${FLOW.grayBorder}`,
                  'bgcolor': checked ? alpha(FLOW.blue, 0.03) : 'transparent',
                  'transition': 'background 0.1s',
                  '&:hover': { bgcolor: alpha(FLOW.blue, 0.05) },
                }}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  sx={{
                    'color': FLOW.grayBorder,
                    '&.Mui-checked': { color: FLOW.blue },
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{
                    fontWeight: checked ? 600 : 400,
                    color: checked ? 'rgba(0,0,0,0.82)' : 'rgba(0,0,0,0.65)',
                    fontSize: '0.85rem',
                  }}>
                    {org.display_name}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Fade>
  )
}

export default OrganizationCheckboxList
